
import { defineComponent, type PropType } from "vue"
import { DateTime } from "luxon"

import MarketingCampaign, { type MarketingCampaignGoal } from "packs/models/MarketingCampaign"
import { buildCampaignGoalDraft } from "./marketingSetupDrafts"

export default defineComponent({
    name: "MarketingCampaignGoalEditor",
    emits: ["saved"],
    props: {
        campaignId: { type: String as PropType<string>, required: true },
        // 雛形の推測に使う施策（実効値）。未指定でも雛形は入れられる。
        campaign: { type: Object as PropType<MarketingCampaign | null>, default: null },
    },
    data() {
        return {
            goal: null as MarketingCampaignGoal | null,
            promptText: "",
            originalPromptText: "",
            maxLength: 5000,
            loading: true,
            saving: false,
            conflicted: false,
            statusMessage: "",
            statusKind: "",
        }
    },
    computed: {
        characterCount(): number {
            return Array.from(this.promptText || "").length
        },
        dirty(): boolean {
            return this.promptText !== this.originalPromptText
        },
        canInsertDraft(): boolean {
            return !this.loading && !this.saving && !`${this.promptText || ""}`.trim()
        },
        canSave(): boolean {
            return !this.loading && !this.saving && this.dirty && this.characterCount <= this.maxLength
        },
    },
    watch: {
        campaignId() {
            void this.load()
        },
    },
    mounted() {
        void this.load()
    },
    methods: {
        applyGoal(goal: MarketingCampaignGoal | null) {
            this.goal = goal
            this.promptText = goal?.prompt_text || ""
            this.originalPromptText = this.promptText
        },
        async load() {
            this.loading = true
            this.conflicted = false
            this.statusMessage = ""
            try {
                const goal = await MarketingCampaign.fetchGoal(this.campaignId)
                this.applyGoal(goal)
            } catch (error: any) {
                this.statusKind = "error"
                this.statusMessage = error?.response?.data?.message || "目標・計画を取得できませんでした。"
            } finally {
                this.loading = false
            }
        },
        async save() {
            if (!this.canSave || !this.goal) return
            this.saving = true
            this.statusKind = "saving"
            this.statusMessage = "保存中です。"
            try {
                const goal = await MarketingCampaign.saveGoal(this.campaignId, this.promptText, this.goal.version)
                this.applyGoal(goal)
                this.conflicted = false
                this.statusKind = "success"
                this.statusMessage = "保存しました。"
                this.$emit("saved", goal)
            } catch (error: any) {
                this.handleSaveError(error)
            } finally {
                this.saving = false
            }
        },
        // 入力欄が空のときだけ下書きを入れる（既存の目標は上書きしない）。保存は利用者が押す。
        insertDraft() {
            if (!this.canInsertDraft) return
            const planning = this.campaign ? MarketingCampaign.planningOf(this.campaign) : null
            this.promptText = buildCampaignGoalDraft({
                targetKeywords: planning?.target_keywords || [],
                targetUrls: planning?.target_urls || [],
                finalConversionEvent: planning?.final_conversion_event || "",
            })
            this.statusKind = "neutral"
            this.statusMessage = "推測値で雛形を入れました。「（推測設定）」の箇所を確認し、必要なら書き換えてから保存してください。"
        },
        cancel() {
            this.promptText = this.originalPromptText
            this.conflicted = false
            this.statusMessage = "編集内容を取り消しました。"
            this.statusKind = "neutral"
        },
        async clear() {
            if (!this.goal?.configured || this.saving) return
            if (!window.confirm("目標・計画を未設定に戻しますか？過去レポートの固定内容は変わりません。")) return
            this.saving = true
            this.statusKind = "saving"
            this.statusMessage = "保存中です。"
            try {
                const goal = await MarketingCampaign.clearGoal(this.campaignId, this.goal.version)
                this.applyGoal(goal)
                this.conflicted = false
                this.statusKind = "success"
                this.statusMessage = "未設定に戻しました。"
                this.$emit("saved", goal)
            } catch (error: any) {
                this.handleSaveError(error)
            } finally {
                this.saving = false
            }
        },
        handleSaveError(error: any) {
            if (error?.response?.status === 409) {
                this.conflicted = true
                this.statusKind = "conflict"
                this.statusMessage = "別の画面で更新されています。入力内容を確認し、最新版を再読み込みしてください。"
                return
            }
            this.statusKind = "error"
            this.statusMessage = error?.response?.data?.message || "保存に失敗しました。時間をおいて再度お試しください。"
        },
        resetTransientStatus() {
            if (this.statusKind === "success" || this.statusKind === "neutral") this.statusMessage = ""
        },
        formatDate(value?: string | null) {
            if (!value) return "-"
            const date = DateTime.fromISO(value)
            return date.isValid ? date.toFormat("yyyy/MM/dd HH:mm") : value
        },
    },
})
